import { isPrerenderUserAgent } from '../util/PrerenderUtil';
import React, { createContext, useContext, useEffect, useRef, useState, useMemo, ReactNode } from 'react';
import initializePendo from '../util/PendoSnippet';
import { observableEndpoints } from '../requests/api-endpoints';
import { getBuyerBookingCounts } from '../requests/generic-requests';
import { isBuyerAccountType } from '../util/AccountUtils';

export interface User {
  firstName: string;
  lastName: string;
  loggedInUsername: string;
  email: string;
  userType: string;
  id: string;
  companyName: string;
  type: string;
  subscriptionType: string;
  createdOn: string;
  [key: string]: any;
}

interface Account {
  id: string;
  firstName: string;
  lastName: string;
  loggedInUsername: string;
  email: string;
  userType: string;
  companyName: string;
  type: string;
  subscriptionType: string;
  createdOn: string;
  [key: string]: any;
}

type PendoUser = User | Account;

interface PendoContextValue {
  initialized: boolean;
  currentUser: PendoUser | null;
}

interface PendoProviderProps {
  children: ReactNode;
  user: PendoUser | null;
}

const PendoContext = createContext<PendoContextValue | undefined>(undefined);

export const usePendo = (): PendoContextValue => {
  const context = useContext(PendoContext);
  if (!context) {
    throw new Error('usePendo must be used within a PendoProvider');
  }
  return context;
};

export const PendoProvider: React.FC<PendoProviderProps> = ({ children, user }) => {
  const initializedRef = useRef<boolean>(false);
  const prevUserRef = useRef<PendoUser | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [currentUser, setCurrentUser] = useState<PendoUser | null>(null);

  useEffect(() => {
    // Initialize Pendo when the component mounts (app starts).
    // Never for a prerender snapshot: a render is not a visit, and Pendo's
    // beacons keep the render open until prerender's page-load timeout.
    if (isPrerenderUserAgent()) return;
    if (!initializedRef.current && window.pendo?.initialize) {
      initializePendo(null, window.pendo.initialize);
      initializedRef.current = true;
      setInitialized(true);
    }
  }, []);

  useEffect(() => {
    if (user && user.id) {
      // User logged in or data updated
      if (window.pendo?.identify) {
        if (isBuyerAccountType(user.type)) {
          getBuyerBookingCounts(observableEndpoints.buyer.buyerBookingCounts)
            .then((counts) => {
              initializePendo(user, window.pendo.identify, { hasFailedPayment: counts.failedCount > 0 });
            })
            .catch(() => {
              initializePendo(user, window.pendo.identify);
            });
        } else {
          initializePendo(user, window.pendo.identify);
        }
      }
      prevUserRef.current = user;
      setCurrentUser(user);
    } else if (!user && prevUserRef.current) {
      // User logged out
      if (window.pendo?.clearSession) {
        window.pendo.clearSession();
      }
      prevUserRef.current = null;
      setCurrentUser(null);
    }
  }, [user]);

  const contextValue = useMemo(() => ({
    initialized,
    currentUser
  }), [initialized, currentUser]);

  return (
    <PendoContext.Provider value={contextValue}>
      {children}
    </PendoContext.Provider>
  );
};
